import { type PhilatelicSet, type StampFromSet, type ReviewState, type Zhihao, PhilatelicItemType, WithoutIdAndReview,
         withZhihaoDefaults, zhihaoForStampInSet } from "@briefmarken_katalog/types";
import type { TranslationKey } from "$lib/i18n";
import { StandardEditorModelBase } from "./StandardEditorModelBase.svelte";
import { validateZhihaoField } from "./validateZhihaoField";

export class SetEditorModel extends StandardEditorModelBase<PhilatelicSet> {
  title: string = $state("");
  description: string = $state("");
  date_of_issue: Date | null = $state<Date | null>(null);
  expiry_date: Date | null = $state<Date | null>(null);
  categories: string[] = $state<string[]>([]);
  zhihao = $state<Zhihao | undefined>(undefined);
  stamps: StampFromSet[] = $state<StampFromSet[]>([]);
  stampsDirty = $state(false);
  /** Prefills the set size in the Zhihao editor — a set's size is its stamp count. */
  suggestedSetSize = $derived(this.stamps.length);
  /**
   * The set's Zhihao as it is stored: the size falls back to the stamp count, and the set
   * itself carries no position — that belongs to the individual stamps (2018-2 T, not
   * 2018-2 (2-1) T). A size entered by hand still wins, for sets captured incompletely or
   * whose official size counts a souvenir sheet.
   */
  effectiveZhihao = $derived(this.zhihaoForStampCount(this.stamps.length));
  isTitleInvalid = $derived(this.title === "");
  isDateInvalid = $derived(this.date_of_issue === null);
  isStampsInvalid = $derived(this.stamps.length === 0);

  constructor({ onCreate = (set: WithoutIdAndReview<PhilatelicSet>) => {},
                onReview = (set: WithoutIdAndReview<PhilatelicSet>, state: ReviewState) => {},
                onDelete = () => {},
                isReadonly = false,
                isReview = false,
                isDelete = false,
                isEdit = false,
                initialValue = null,
                dialogTitle = 'editor.setEditorTitle' as TranslationKey,
                dialogSubtitle = 'editor.setEditorSubtitle' as TranslationKey,
              } :
              {
                onCreate?: (set: WithoutIdAndReview<PhilatelicSet>) => void,
                onReview?: (set: WithoutIdAndReview<PhilatelicSet>, state: ReviewState) => void,
                onDelete?: () => void,
                isReadonly?: boolean,
                isReview?: boolean,
                isDelete?: boolean,
                isEdit?: boolean,
                initialValue?: PhilatelicSet | null,
                dialogTitle?: TranslationKey,
                dialogSubtitle?: TranslationKey,
              } = {}
            )
  {
    super({ onCreate, onReview, onDelete, isReadonly, isReview, isDelete, isEdit,
            hasInitialValue: initialValue !== null, dialogTitle, dialogSubtitle });

    if (initialValue) {
      this.title = initialValue.title;
      this.description = initialValue.description;
      this.date_of_issue = initialValue.date_of_issue;
      this.expiry_date = initialValue.expiry_date ?? null;
      this.categories = initialValue.categories ?? [];
      this.zhihao = initialValue.zhihao;
      for (const stamp of initialValue.stamps)
      {
        this.stamps.push(stamp);
      }
      const { stamps: _, ...rest } = this.buildItem();
      this.initialSnapshot = JSON.stringify(rest);
    }
  }

  protected computeIsDirty(): boolean {
    if (this.initialSnapshot === null) return true;
    const { stamps: _, ...rest } = this.buildItem();
    return this.stampsDirty || JSON.stringify(rest) !== this.initialSnapshot;
  }

  validate(): [boolean, TranslationKey | '']
  {
    if (this.title === "")
    {
      return [false, 'validation.titleEmpty'];
    }
    if (this.date_of_issue === null)
    {
      return [false, 'validation.dateNotSet'];
    }
    if (this.stamps.length === 0)
    {
      return [false, 'validation.noStampsInSet'];
    }
    return validateZhihaoField(this.effectiveZhihao) ?? [true, ''];
  }

  /**
   * The set's Zhihao for a given number of stamps. Taking the count as a parameter lets the
   * "add stamp" dialog preview the size the set is about to have.
   */
  zhihaoForStampCount(count: number): Zhihao | undefined {
    if (!this.zhihao) return undefined;
    const { position: _dropped, ...withoutPosition } = this.zhihao;
    return withZhihaoDefaults(withoutPosition, { setSize: count || undefined });
  }

  /** The Zhihao of the stamp in slot `index` — everything but its position comes from the set. */
  stampZhihao(index: number): Zhihao | undefined {
    return zhihaoForStampInSet(this.effectiveZhihao, index + 1);
  }

  buildItem(): WithoutIdAndReview<PhilatelicSet> {
    return {
      title: this.title,
      description: this.description,
      date_of_issue: this.date_of_issue!,
      expiry_date: this.expiry_date ?? undefined,
      categories: this.categories.length > 0 ? this.categories : undefined,
      zhihao: this.effectiveZhihao,
      pictures: [],
      // The stamps' Zhihao is derived here rather than stored per stamp, so editing the
      // set's Zhihao or adding a stamp cannot leave the others behind.
      stamps: this.stamps.map((stamp, i) => ({ ...stamp, zhihao: this.stampZhihao(i) })),
      type: PhilatelicItemType.Set,
    };
  }

  reset() {
    this.title = "";
    this.description = "";
    this.date_of_issue = null;
    this.expiry_date = null;
    this.categories = [];
    this.zhihao = undefined;
    this.stamps = [];
  }

  onStampAdded(stamp: StampFromSet)
  {
    this.stamps.push(stamp);
    this.stampsDirty = true;
  }

  updateStamp(index: number, stamp: StampFromSet)
  {
    this.stamps[index] = stamp;
    this.stampsDirty = true;
  }

  /** Removes a stamp; the ones behind it move up and are renumbered accordingly. */
  removeStamp(index: number)
  {
    if (index < 0 || index >= this.stamps.length) return;
    this.stamps.splice(index, 1);
    this.stampsDirty = true;
  }

  /**
   * Swaps a stamp with its neighbour. Stamps are captured in the order they are added, so
   * this is the way to correct that order — the Zhihao positions follow from it.
   */
  moveStamp(index: number, offset: -1 | 1)
  {
    const target = index + offset;
    if (target < 0 || target >= this.stamps.length) return;
    const moved = this.stamps[index];
    this.stamps[index] = this.stamps[target];
    this.stamps[target] = moved;
    this.stampsDirty = true;
  }
}
